import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useVForm } from 'shared/forms';
import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { ICreateEnvio } from 'shared/types';
import { t } from 'i18next';
import env from 'config/env';
import { enUS, ptBR } from 'date-fns/locale';
import { useHasPermissionAcao } from 'shared/hooks';
import { EnvioService } from 'shared/services/envio/EnvioService';
import { OrganizationsService } from 'shared/services/organizations/organizationsService';
import { IKeyValue, dateToLocalISO, listagemCanalToIKeyValue, listagemOrgToIKeyValue, stringToLocalISO } from 'shared/utils';
import { CanaisService } from 'shared/services/canais/canaisService';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';


const validationSchema = yup.object({
  OrgCodigo: yup.number().moreThan(0),
  CanalId: yup.number(),
  DhEnvioIni: yup.string(),
  DhEnvioFin: yup.string(),
});

export const DetalheDeEnvio: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const { idCanal } = useParams<'idCanal'>();
  const novo = isNaN(Number(id)) ? true : false;

  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('envio', 'CRE');
  const hasPermitionUpdate = useHasPermissionAcao('envio', 'UPD');

  const localeSel = (env.language === 'en' ? enUS : ptBR);
  const localeMask = (env.language === 'en' ? '__:__ _M' : '__:__');
  const localeInputFormat = (env.language === 'en' ? 'hh:mm a' : 'HH:mm');

  const lenDate = (env.language === 'en' ? 22 : 19);


  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, isSaveAndClose } = useVForm();

  const { register, handleSubmit, setValue, watch, reset, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const selectTipo = watch('CanalId');
  const selectOrg = watch('OrgCodigo');
  const dateTimePickerInicio = watch('DhEnvioIni');
  const dateTimePickerFim = watch('DhEnvioFin');
  const dataNow = new Date();

  const handleChangeDhInicio = (valor: string) => setValue('DhEnvioIni', valor);
  const handleChangeDhFim = (valor: string) => setValue('DhEnvioFin', valor);

  dateTimePickerInicio === undefined ? handleChangeDhInicio(dataNow.toDateString()) : watch('DhEnvioIni');
  dateTimePickerFim === undefined ? handleChangeDhFim(dataNow.toDateString()) : watch('DhEnvioIni');
  
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCanal, setIsLoadingCanal] = useState(true);

  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [messageConfirmacao, setMessageConfirmacao] = useState('');
  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);
  const [listKeyValueOrg, setListKeyValueOrg] = useState<IKeyValue[]>([]);
  useEffect(() => {
    setAlert(null);
    if (!novo) {
      setIsLoading(true);

      EnvioService.getById(Number(id), Number(idCanal))
        .then((result) => {
          // console.log(result);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/envio/listagem');
          } else {
            reset(result);
          }
          setIsLoading(false);
        });
    }

  }, [id]);

  useEffect(() => {
    setIsLoading(true);

    OrganizationsService.getAll()
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setListKeyValueOrg(listagemOrgToIKeyValue(result.rows));
        }
      });
  }, []);

  useEffect(() => {
    setIsLoadingCanal(true);

    CanaisService.getAll()
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
  }, []);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.organizationSeletion.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    EnvioService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'error',
          });
          navigate('/envio/listagem');
        }
      })
      .catch(reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateEnvio) => {
    setAlert(null);
    setIsSubmitting(true);
    if (novo) {
      EnvioService
        .create(data)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/envio/listagem');
            } setAlert({
              text: t('messages.success.incluido'),
              type: 'success',
            });
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      EnvioService
        .update(data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/envio/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'),
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };

  // console.log(dateTimePickerInicio);
  // console.log(dateTimePickerFim);
  // console.log(dateTimePickerInicio < dateTimePickerFim);

  useEffect(() =>{
    if(dateTimePickerInicio != undefined){
      if (dateTimePickerInicio.substring(11,19) > dateTimePickerFim.substring(11,19)) {
        handleChangeDhInicio(dateTimePickerFim);
        setAlert({
          text: t('messages.error.horario'),
          type: 'error',
        });
      }
    }
  },[dateTimePickerInicio,dateTimePickerFim]);

  return (
    <LayoutBaseDePagina

      titulo={novo ? t('pages.envio.newenvio') : hasPermitionUpdate ? t('pages.envio.changeenvio') : t('pages.envio.viewenvio')}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeEnvio'

          mostrarBotaoNovo={novo || hasPermitionCreate}
          mostrarBotaoApagar={false}
          mostrarBotaoSalvar={novo || hasPermitionUpdate}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/envio/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/envio/detalhe/')}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeEnvio' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={6} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-tipo'>{`${t('pages.envio.Organizacao')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || !novo}
                        error={errors?.ORG_CD_NM?.type ? true : false}
                        labelId='label-select-tipo'
                        id='ORG_CD_NM'

                        input={<OutlinedInput label={`${t('pages.envio.Organizacao')} *`} />}
                        {...register('OrgCodigo')}
                        value={selectOrg || id || ''}

                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueOrg.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.ORG_CD_NM?.type && <InputError type={errors.ORG_CD_NM.type} field='ORG_CD_NM' />}

                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-tipo'>{`${t('pages.envio.Canal')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || !novo}
                        error={errors?.CanalNome?.type ? true : false}
                        labelId='label-select-tipo'
                        id='CanalNome'

                        input={<OutlinedInput label={`${t('pages.envio.Canal')} *`} />}
                        {...register('CanalId')}
                        value={selectTipo || idCanal || ''}

                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueCanal.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.CanalNome?.type && <InputError type={errors.CanalNome.type} field='CanalNome' />}

                  </Grid>

                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={isSubmitting || isLoading || (!hasPermitionUpdate && novo)}
                          label={`${t('pages.workflows.dhPeriodoInicio')} *`}
                          value={dateTimePickerInicio}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['hours', 'minutes']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhInicio(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhInicio((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) => <TextField id='DhEnvioIni' {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.DhEnvioIni?.type && <InputError type={errors.DhEnvioIni.type} field='DhEnvioIni' />}
                  </Grid>

                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={isSubmitting || isLoading || (!hasPermitionUpdate && novo)}
                          label={`${t('pages.workflows.dhPeriodoFim')} *`}
                          value={dateTimePickerFim}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['hours', 'minutes']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhFim(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhFim((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) => <TextField id='DhEnvioFin' {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.DhEnvioFin?.type && <InputError type={errors.DhEnvioFin.type} field='DhEnvioFin' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}
    </LayoutBaseDePagina >
  );
};
function setStateSnack(arg0: { open: boolean; severity: string; message: string; }) {
  throw new Error('Function not implemented.');
}

function setListKeyValueOrg(arg0: any) {
  throw new Error('Function not implemented.');
}

